import { useEffect, useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { setBao3d } from "./useBaoModel";

const D = THREE.MathUtils.degToRad;
const FACING = { front: 0, back: Math.PI, profile: Math.PI / 2 };
const BONES = ["Hips", "Spine02", "LeftUpLeg", "LeftLeg", "LeftFoot", "LeftToeBase", "RightUpLeg", "RightLeg", "RightFoot", "RightToeBase", "LeftArm", "LeftForeArm", "LeftHand", "RightArm", "RightForeArm", "RightHand"];
const v1 = new THREE.Vector3(), v2 = new THREE.Vector3(), q1 = new THREE.Quaternion(), q2 = new THREE.Quaternion();

/** Rotate `bone` (world space) so that bone→child points along `dir` (character space). */
const aim = (bone, child, dir, rootQ) => {
  bone.updateMatrixWorld(true);
  const cur = child.getWorldPosition(v2).sub(bone.getWorldPosition(v1)).normalize();
  const want = dir.clone().applyQuaternion(rootQ).normalize();
  const q = new THREE.Quaternion().setFromUnitVectors(cur, want);
  bone.parent.getWorldQuaternion(q1); bone.getWorldQuaternion(q2);
  bone.quaternion.copy(q1.invert().multiply(q.multiply(q2)));
  bone.updateMatrixWorld(true);
};
const limbDir = (out, swing, abd) => new THREE.Vector3(out * Math.sin(D(abd)) * Math.cos(D(swing)), -Math.cos(D(abd)) * Math.cos(D(swing)), Math.sin(D(swing)));

const Actor = ({ model, pose, view, clip }) => {
  const root = model.root;
  const rig = useMemo(() => {
    const b = {}; root.traverse((o) => { const n = BONES.find((x) => o.name === x || o.name.endsWith(`:${x}`)); if (n && o.isBone && !b[n]) b[n] = o; });
    const rest = Object.fromEntries(Object.entries(b).map(([n, o]) => [n, o.quaternion.clone()]));
    root.updateMatrixWorld(true);
    const toes = () => Math.min(b.LeftToeBase.getWorldPosition(v1).y, b.RightToeBase.getWorldPosition(v1).y, b.LeftFoot.getWorldPosition(v1).y, b.RightFoot.getWorldPosition(v1).y);
    return { b, rest, hipPos: b.Hips.position.clone(), toes, ground: toes() - root.position.y };
  }, [root]);
  const mixer = useMemo(() => new THREE.AnimationMixer(model.scene), [model]);
  const act = useRef(null);
  useEffect(() => {
    act.current?.fadeOut(0.25); act.current = null;
    const a = clip && model.clips[clip];
    if (a) { act.current = mixer.clipAction(a).reset().fadeIn(0.25).play(); } else mixer.stopAllAction();
  }, [clip, mixer, model]);

  const perf = useRef({ n: 0, t: 0 });
  useFrame((st, dt) => {
    const pf = perf.current;
    if (pf.n < 180) { pf.n += 1; pf.t += dt; if (pf.n === 180 && pf.t / 180 > 1 / 20) setBao3d(false); }
    root.rotation.y = FACING[view] ?? 0; root.position.y = 0;
    if (act.current) { mixer.update(dt); return; }
    const { b, rest } = rig, rq = root.getWorldQuaternion(new THREE.Quaternion());
    Object.entries(rest).forEach(([n, q]) => b[n].quaternion.copy(q));
    b.Hips.position.copy(rig.hipPos);
    root.updateMatrixWorld(true);
    const shift = new THREE.Vector3(-(pose.px || 0) * 0.0042, 0, 0).applyQuaternion(rq), hw = b.Hips.getWorldPosition(new THREE.Vector3()).add(shift);
    b.Hips.position.copy(b.Hips.parent.worldToLocal(hw));
    root.updateMatrixWorld(true);
    const lean = D((pose.torso || 0) * 0.6), breathe = D(Math.sin(st.clock.elapsedTime * 1.7) * 1.2);
    const axis = new THREE.Vector3(view === "profile" ? 1 : 0, 0, view === "profile" ? 0 : 1).applyQuaternion(rq);
    b.Spine02.getWorldQuaternion(q2);
    b.Spine02.parent.getWorldQuaternion(q1);
    b.Spine02.quaternion.copy(q1.clone().invert().multiply(new THREE.Quaternion().setFromAxisAngle(axis, view === "profile" ? lean + breathe : -lean).multiply(q2)));
    b.Spine02.updateMatrixWorld(true);
    const tw = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), D(pose.twist || 0));
    if (pose.twist) {
      b.Spine02.getWorldQuaternion(q2); b.Spine02.parent.getWorldQuaternion(q1);
      b.Spine02.quaternion.copy(q1.clone().invert().multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0).applyQuaternion(rq), D(pose.twist)).multiply(q2)));
      b.Spine02.updateMatrixWorld(true);
    }
    [["Left", 1, pose.L, pose.aL], ["Right", -1, pose.R, pose.aR]].forEach(([s, out, l, a]) => {
      const abd = 3 + (l.abd || 0);
      aim(b[`${s}UpLeg`], b[`${s}Leg`], limbDir(out, l.h, abd), rq);
      aim(b[`${s}Leg`], b[`${s}Foot`], limbDir(out, l.h - l.k, abd), rq);
      const sw = view === "back" && a.a > 30 ? Math.max(a.s || 0, 25) : a.s || 0, ab = view === "profile" ? 8 : a.a ?? 8;
      aim(b[`${s}Arm`], b[`${s}ForeArm`], limbDir(out, sw, ab).applyQuaternion(tw), rq);
      aim(b[`${s}ForeArm`], b[`${s}Hand`], limbDir(out, sw + (a.e || 0), ab + (a.e || 0) * 0.3).applyQuaternion(tw), rq);
    });
    root.position.y = rig.ground - rig.toes();
  });
  return <primitive object={root} />;
};

/** In-game 3D Bao driven by the same clinical pose as the 2D rig (hips/knees/ankles/arms), or playing a clip (walk, celebrate). */
export const Bao3DActor = ({ model, pose, view = "profile", clip = null, testId = "bao-3d-ingame" }) => (
  <div className="h-full w-full" data-testid={testId} data-view={view} data-clip={clip || "driven"}>
    <Canvas dpr={[1, 1.5]} gl={{ alpha: true, antialias: true, toneMapping: THREE.ACESFilmicToneMapping, outputColorSpace: THREE.SRGBColorSpace }} camera={{ position: [0, 0.9, 5.4], fov: 20 }} onCreated={({ camera, gl }) => { camera.lookAt(0, 0.88, 0); gl.setClearColor(0x000000, 0); }}>
      <hemisphereLight args={["#FFF4E0", "#8FA8A0", 1.4]} />
      <directionalLight position={[2, 4, 3]} intensity={2.2} />
      <directionalLight position={[-3, 2, -2]} intensity={1} color="#7FE3D8" />
      <Actor model={model} pose={pose} view={view} clip={clip} />
    </Canvas>
  </div>
);
